<template>
  <div class="boost-expired-modal">
    <div class="overlay" @click="$emit('close')"></div>
    <transition name="pop-in" appear>
      <div
        class="card"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('boost_expired_title')"
      >
        <BaseIcon
          ref="closeButton"
          icon-file="close-white"
          :width="16"
          :height="16"
          class="close-icon"
          tabindex="0"
          @click="$emit('close')"
          @keydown.enter="$emit('close')"
        />

        <div class="hero">
          <div class="ring">
            <div class="multiplier">{{ BOOST_EXPOSURE_MULTIPLIER }}×</div>
            <div class="multiplier-label">
              {{ $t('boost_active_multiplier_label') }}
            </div>
          </div>

          <div class="badge">
            <BaseIcon
              icon-file="icon-rocket-white"
              :width="14"
              :height="14"
              class="badge-icon"
            />
            {{ $t('boost_expired_badge') }}
          </div>

          <h1 class="title">{{ $t('boost_expired_title') }}</h1>
          <p class="text">
            {{
              $t('boost_expired_text', {
                multiplier: BOOST_EXPOSURE_MULTIPLIER
              })
            }}
          </p>
        </div>

        <div class="offer">
          <div v-if="product" class="offer-card">
            <div v-if="offerOpen" class="offer-tag">
              {{
                $t('boost_expired_offer_tag', {
                  hours: REBOOST_OFFER_WINDOW_HOURS
                })
              }}
            </div>
            <div>
              <div class="offer-title">
                {{
                  $t('boost_expired_offer_title', { days: product.quantity })
                }}
              </div>
              <div class="offer-text">{{ $t('boost_expired_offer_text') }}</div>
            </div>
            <div class="offer-prices">
              <div v-if="saving" class="offer-ordinary-price">
                {{
                  $t('general_price', {
                    amount: formatAmount(product.originalPrice)
                  })
                }}
              </div>
              <div class="offer-price">
                {{
                  $t('general_price', {
                    amount: formatAmount(product.priceInclVat)
                  })
                }}
              </div>
            </div>
          </div>

          <BaseButton
            icon-file="icon-rocket-white"
            class="purple larger rounded full-width cta-button"
            @click="buyBoost"
          >
            {{
              saving
                ? $t('boost_expired_cta_save', { amount: formatAmount(saving) })
                : $t('boost_expired_cta')
            }}
          </BaseButton>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { lbApiBeta } from '@/utils/axiosConfig';
import {
  BOOST_EXPOSURE_MULTIPLIER,
  REBOOST_OFFER_WINDOW_HOURS
} from '@/utils/constants/boost';
import { reboostOfferClosesAt } from '@/utils/helpers/reboostWindow';
import { formatAmount } from '@/filters/currency';
import { startTimeTicker, timeTicker } from '@/utils/helpers/relativeTime';
import BaseButton from '@/components/BaseButton.vue';
import BaseIcon from '@/components/BaseIcon.vue';

// The re-boost is sold as this product. Asked for with the ad's id, the
// payment API returns it discounted (with the regular price as
// originalPrice) when the ad is entitled to the re-boost offer.
const REBOOST_DAYS = 7;

const SCROLL_LOCK_NAME = 'BoostExpiredModal';

// Consumables variant of the "Din Boost" modal once the boost has expired:
// the result panel (design 4a) — full screen on mobile, a centered card
// on desktop. /api/boost-summary's activeUntil — the instant the boost
// ended — decides whether the 7-day re-boost is still a personal offer;
// the CTA opens the purchase modal.
export default {
  name: 'BoostExpiredModal',
  components: {
    BaseButton,
    BaseIcon
  },

  data() {
    return {
      BOOST_EXPOSURE_MULTIPLIER,
      REBOOST_OFFER_WINDOW_HOURS,
      activeUntil: null,
      lastFocusedElement: null
    };
  },

  computed: {
    ...mapGetters({
      primaryProposition: 'myPropositions/primaryProposition',
      products: 'klarnaPayments/getProducts'
    }),

    product() {
      return this.products.find(p => p.quantity === REBOOST_DAYS) || null;
    },

    // What the offer saves against the regular price: the payment API marks
    // a discounted product with originalPrice. 0 when there is no discount.
    saving() {
      const original = Number(this.product?.originalPrice);
      const price = Number(this.product?.priceInclVat);
      return original > price ? original - price : 0;
    },

    // Whether the personal offer is still open; false while the boost's
    // end is unknown. The tag names the offer's length, a fixed
    // REBOOST_OFFER_WINDOW_HOURS — it is how long the offer runs for
    // everyone, not a countdown of this reader's hours left, which read
    // as a longer offer the earlier the panel was opened.
    offerOpen() {
      const closesAt = reboostOfferClosesAt(this.activeUntil);
      return closesAt !== null && closesAt > timeTicker.now;
    }
  },

  created() {
    startTimeTicker();
    this.lastFocusedElement = document.activeElement;
    this.addDisableScroll(SCROLL_LOCK_NAME);
    this.loadSummary();
    // With the ad's id the payment API answers with the discounted re-boost
    // product the ad is entitled to; the store caches per type + ad.
    this.fetchProducts({
      productType: 'extraVisibility',
      propositionId: this.primaryProposition?.propositionId
    });
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    if (this.lastFocusedElement && this.lastFocusedElement !== document.body) {
      this.$nextTick(() => this.$refs.closeButton?.$el?.focus());
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    this.removeDisableScroll(SCROLL_LOCK_NAME);
    this.lastFocusedElement?.focus();
  },

  methods: {
    formatAmount,
    ...mapActions({
      fetchProducts: 'klarnaPayments/fetchProducts'
    }),

    ...mapMutations({
      addDisableScroll: 'ui/addDisableScroll',
      removeDisableScroll: 'ui/removeDisableScroll',
      setExtraVisibilityPaymentProposition: 'extraVisibility/setProposition',
      setExtraVisibilityPaymentVisible: 'extraVisibility/setVisible'
    }),

    async loadSummary() {
      try {
        const { data } = await lbApiBeta.get(
          `/api/boost-summary?propositionId=${this.primaryProposition?.propositionId}`
        );
        this.activeUntil = data.activeUntil || null;
      } catch (e) {
        window.Logger.error(e);
      }
    },

    buyBoost() {
      this.setExtraVisibilityPaymentProposition(this.primaryProposition);
      this.setExtraVisibilityPaymentVisible(true);
      this.$emit('close');
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.boost-expired-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ($desktop) {
    padding: 24px;
  }
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(11, 24, 44, 0.55);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

// Mobile: a full-screen panel. Desktop: a centered card.
.card {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: #fff;
  text-align: center;

  @media ($desktop) {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    left: auto;
    width: 100%;
    max-width: 360px;
    max-height: calc(100vh - 48px);
    border-radius: 22px;
    box-shadow: 0 24px 60px rgba(11, 24, 44, 0.35);
  }
}

.close-icon {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
  cursor: pointer;
}

.hero {
  position: relative;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 40px 24px;
  color: #fff;
  background: linear-gradient(160deg, #b05df2 0%, #9f41e9 45%, #5b1fa3 100%);

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }

  &::before {
    top: -40px;
    right: -90px;
    width: 280px;
    height: 280px;
  }

  &::after {
    bottom: 40px;
    left: -80px;
    width: 220px;
    height: 220px;
  }
}

.ring {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 150px;
  height: 150px;
  border: 4px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 12px rgba(255, 255, 255, 0.12);
}

.multiplier {
  font-family: $font-headings;
  font-weight: 700;
  font-size: 56px;
  line-height: 1;
  letter-spacing: -0.04em;
}

.multiplier-label {
  margin-top: 6px;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 32px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
}

.title {
  position: relative;
  margin: 14px 0 0;
  font-family: $font-headings;
  font-weight: 700;
  font-size: 30px;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.text {
  position: relative;
  max-width: 300px;
  margin: 10px 0 0;
  font-size: 16px;
  line-height: 1.5;
  opacity: 0.92;
}

.offer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 20px 28px;
  background: #fff;
}

.offer-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 18px 16px;
  border: 1.5px solid #e9dcf8;
  border-radius: 18px;
  background: #faf6fe;
  text-align: left;
}

.offer-tag {
  position: absolute;
  top: -11px;
  left: 16px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #9f41e9;
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.offer-title {
  font-weight: 600;
  font-size: 15px;
  line-height: 1.2;
  color: var(--Text-Primary);
}

.offer-text {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.4;
  color: #5b6270;
}

.offer-prices {
  flex-shrink: 0;
  text-align: right;
  white-space: nowrap;
}

.offer-ordinary-price {
  font-size: 13px;
  line-height: 1.2;
  color: #8a919c;
  text-decoration: line-through;
}

.offer-price {
  font-family: $font-headings;
  font-weight: 700;
  font-size: 30px;
  line-height: 1;
  letter-spacing: -0.03em;
  color: #9f41e9;
}
</style>
